<template>
  <div class="units-select-paginated-wrapper">
    <v-select
      :class="['units-select-paginated', { 'units-select-paginated--dropup': dropup }]"
      v-model="model"
      :options="formattedOptions"
      :filterable="false"
      :dropup="dropup"
      :append-to-body="appendToBody"
      :calculate-position="calculatePosition"
      @search="onSearch"
      :multiple="isMultiple"
      :clearable="isMultiple"
      :disabled="disabled"
      ref="selectable"
      @input="onInput"
      label="label"
      :reduce="(item) => item.value"
      :placeholder="placeholder"
    >
      <template #list-footer>
        <li class="pagination">
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!hasPrevPage"
            @click="prevPage"
          >
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!hasNextPage"
            @click="nextPage"
          >
            <feather-icon icon="ChevronRightIcon" size="18" />
          </b-button>
        </li>
      </template>
    </v-select>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { debounce } from "lodash";
import { BButton } from "bootstrap-vue";
import axios from "@axios";
import store from "@/store";
import {
  filterMainUnitsIfPresent,
  hasMainUnitFlag,
  isMainUnit,
} from "@/libs/planning/unitFilterByActivityType";

function getFirstDefinedValue(candidateValues = []) {
  const matchedValue = candidateValues.find((value) => value != null && String(value).trim() !== "");
  return matchedValue == null ? "" : matchedValue;
}

function extractUnitFields(rawUnit = {}) {
  const unit = rawUnit && rawUnit.unit ? rawUnit.unit : rawUnit;
  const description = getFirstDefinedValue([
    rawUnit.description,
    rawUnit.label,
    rawUnit.name,
    unit.description,
    unit.label,
    unit.name,
  ]);
  const sirh = getFirstDefinedValue([
    rawUnit.sirh,
    rawUnit.SIRH,
    rawUnit.unit_sirh,
    rawUnit.code_sirh,
    rawUnit.branch_code,
    unit.sirh,
    unit.SIRH,
    unit.unit_sirh,
    unit.code_sirh,
    unit.branch_code,
  ]);
  const rems = getFirstDefinedValue([
    rawUnit.rems,
    rawUnit.REMS,
    rawUnit.unit_rems,
    rawUnit.code_rems,
    unit.rems,
    unit.REMS,
    unit.unit_rems,
    unit.code_rems,
  ]);
  return { description, sirh, rems };
}

function formatUnitLabel(description, sirh, rems) {
  const desc = description != null && String(description).trim() !== "" ? String(description) : "";
  const remsText = rems != null && String(rems).trim() !== "" ? `REMS: ${String(rems)}` : "No REMS";
  const sirhText = sirh != null && String(sirh).trim() !== "" ? `SIRH: ${String(sirh)}` : "No SIRH";
  if (!desc) return `${remsText}, ${sirhText}`;
  return `${desc} (${remsText}, ${sirhText})`;
}

function extractIdValue(value) {
  if (value == null) return null;
  if (typeof value === "object") {
    if (value.value != null) return value.value;
    if (value.id != null) return value.id;
    return null;
  }
  return value;
}

function getUnitDeactivation(rawUnit = {}) {
  const unit = rawUnit && rawUnit.unit ? rawUnit.unit : rawUnit;
  const deactivationCandidates = [
    rawUnit.deactivation,
    rawUnit.router && rawUnit.router.deactivation,
    unit && unit.deactivation,
    unit && unit.router && unit.router.deactivation,
  ];
  const matched = deactivationCandidates.find((value) => value != null);
  return matched == null ? null : matched;
}

function isUnitDisabledByDeactivation(rawUnit = {}) {
  return getUnitDeactivation(rawUnit) != null;
}

export default {
  name: "UnitsSelectPaginated",
  components: {
    vSelect,
    BButton,
  },
  props: {
    value: {
      type: [Array, Number, null],
      default: null,
    },
    multiple: {
      type: [Boolean, Number],
      default: true,
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    initialOptions: {
      type: Array,
      default: () => [],
    },
    limit: {
      type: Number,
      default: 10,
    },
    dropup: {
      type: Boolean,
      default: false,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    calculatePosition: {
      type: Function,
      default: undefined,
    },
    excludeDisabled: {
      type: Boolean,
      default: false,
    },
    mainUnitOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      model: null,
      search: "",
      currentPage: 1,
      meta: null,
      options: [],
      valueLabelCache: {},
      mainUnitByIdCache: {},
    };
  },
  created() {
    this.syncModelFromValue();
  },
  async mounted() {
    await this.ensureUnitStoreData();
    this.fillCacheFromInitialOptions();
    this.fetchData().then(() => this.fetchMissingLabels());
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    value() {
      this.syncModelFromValue();
      this.$nextTick(() => this.fetchMissingLabels());
    },
    initialOptions: {
      deep: true,
      handler() {
        this.syncModelFromValue();
        this.fillCacheFromInitialOptions();
      },
    },
    options() {
      this.fillCacheFromOptions();
    },
    model() {
      this.fillCacheFromOptions();
    },
    mainUnitOnly() {
      this.currentPage = 1;
      this.search = "";
      this.fetchData()
        .then(() => this.fetchMissingLabels())
        .catch(() => {});
    },
  },
  computed: {
    isMultiple() {
      return this.multiple || this.multiple === 1;
    },
    selectedIds() {
      if (this.isMultiple && Array.isArray(this.model)) {
        return this.model
          .map((item) => extractIdValue(item))
          .filter((item) => item != null);
      }
      const selectedId = extractIdValue(this.model);
      return selectedId != null ? [selectedId] : [];
    },
    combinedOptions() {
      const fromApi = (this.options || []).map((item) => {
        const { description, sirh, rems } = extractUnitFields(item);
        return {
          value: item.id,
          label: formatUnitLabel(description, sirh, rems),
        };
      });
      const selectedIdsSet = new Set(this.selectedIds);
      const optionsForSelected = this.selectedIds.map((id) => ({
        value: id,
        label: this.valueLabelCache[id] != null ? this.valueLabelCache[id] : String(id),
      }));
      const fromApiNotSelected = fromApi.filter((o) => !selectedIdsSet.has(o.value));
      return [...optionsForSelected, ...fromApiNotSelected];
    },
    formattedOptions() {
      return this.combinedOptions;
    },
    hasNextPage() {
      if (this.meta) {
        return this.meta.current_page < this.meta.last_page;
      }
      return false;
    },
    hasPrevPage() {
      if (this.meta) {
        return this.meta.current_page > 1;
      }
      return false;
    },
  },
  methods: {
    async ensureUnitStoreData() {
      const existingItems = store.state?.unit?.items || [];
      if (existingItems.length) return;
      try {
        const params = { max: 500 };
        if (this.mainUnitOnly) params.is_main_unit = 1;
        await store.dispatch("unit/getAll", params);
      } catch (_) {
      }
    },
    getUnitFromStoreById(unitId) {
      if (unitId == null) return null;
      const items = store.state?.unit?.items || [];
      return items.find((item) => Number(item.id) === Number(unitId)) || null;
    },
    mergeUnitWithStoreData(unitItem) {
      if (!unitItem || unitItem.id == null) return unitItem;
      const storeUnit = this.getUnitFromStoreById(unitItem.id);
      if (!storeUnit) return unitItem;
      return { ...unitItem, ...storeUnit };
    },
    shouldFetchUnitDetails(unitItem) {
      const { sirh, rems } = extractUnitFields(unitItem);
      return !sirh || !rems;
    },
    async enrichUnitWithDetails(unitItem) {
      if (!unitItem || unitItem.id == null) return unitItem;
      const mergedUnit = this.mergeUnitWithStoreData(unitItem);
      if (!this.shouldFetchUnitDetails(mergedUnit)) return mergedUnit;
      try {
        const { data } = await axios.get(`/v1/unit/${mergedUnit.id}`);
        const detailedUnit = (data && data.data) || data;
        if (!detailedUnit || detailedUnit.id == null) return mergedUnit;
        return { ...mergedUnit, ...detailedUnit };
      } catch (_) {
        return mergedUnit;
      }
    },
    async enrichUnitsWithDetails(unitItems = []) {
      const enrichedUnits = await Promise.all(
        (unitItems || []).map((unitItem) => this.enrichUnitWithDetails(unitItem))
      );
      return enrichedUnits;
    },
    async isMainUnitById(unitId) {
      const normalizedUnitId = Number(unitId);
      if (!Number.isFinite(normalizedUnitId)) return false;
      if (this.mainUnitByIdCache[normalizedUnitId] != null) {
        return this.mainUnitByIdCache[normalizedUnitId] === true;
      }
      try {
        const { data } = await axios.get(`/v1/unit/${normalizedUnitId}`);
        const unit = (data && data.data) || data;
        const result = isMainUnit(unit);
        this.$set(this.mainUnitByIdCache, normalizedUnitId, result);
        return result;
      } catch (_) {
        this.$set(this.mainUnitByIdCache, normalizedUnitId, false);
        return false;
      }
    },
    async filterUnitsByMainUnitWithFallback(unitItems = []) {
      const list = Array.isArray(unitItems) ? unitItems : [];
      if (!this.mainUnitOnly) return list;
      const filteredByInlineFlag = filterMainUnitsIfPresent(list);
      const hasInlineMainUnitFlag = list.some((unit) => hasMainUnitFlag(unit));
      if (hasInlineMainUnitFlag) return filteredByInlineFlag;
      const checks = await Promise.all(
        list.map(async (unitItem) => {
          const unitId = unitItem && unitItem.id != null ? unitItem.id : null;
          if (unitId == null) return null;
          const allowed = await this.isMainUnitById(unitId);
          return allowed ? unitItem : null;
        })
      );
      return checks.filter((unitItem) => unitItem != null);
    },
    fillCacheFromInitialOptions() {
      const initial = Array.isArray(this.initialOptions) ? this.initialOptions : [];
      initial.forEach((o) => {
        const optionId = extractIdValue(o);
        if (o && optionId != null && o.label != null) {
          this.$set(this.valueLabelCache, optionId, o.label);
        }
      });
    },
    fillCacheFromOptions() {
      const opts = this.options || [];
      const ids = this.selectedIds || [];
      const idsSet = new Set(ids);
      opts.forEach((item) => {
        if (idsSet.has(item.id)) {
          const { description, sirh, rems } = extractUnitFields(item);
          this.$set(
            this.valueLabelCache,
            item.id,
            formatUnitLabel(description, sirh, rems)
          );
        }
      });
    },
    syncModelFromValue() {
      const val = this.value;
      if (this.isMultiple) {
        this.model = Array.isArray(val)
          ? val.map((item) => extractIdValue(item)).filter((item) => item != null)
          : [];
      } else {
        this.model = extractIdValue(val);
      }
    },
    onInput() {
      this.$emit("input", this.model);
    },
    filterUnitsByDisabledStatus(unitItems = []) {
      if (!this.excludeDisabled) return unitItems;
      return (unitItems || []).filter((unitItem) => !isUnitDisabledByDeactivation(unitItem));
    },
    removeDisabledUnitFromModel(unitId) {
      if (!this.excludeDisabled || unitId == null) return;
      this.removeUnitFromModel(unitId);
    },
    removeUnitFromModel(unitId) {
      if (unitId == null) return;
      if (this.isMultiple) {
        if (!Array.isArray(this.model)) return;
        const next = this.model
          .map((item) => extractIdValue(item))
          .filter((item) => Number(item) !== Number(unitId));
        if (next.length === this.model.length) return;
        this.model = next;
        this.onInput();
        return;
      }
      if (Number(extractIdValue(this.model)) !== Number(unitId)) return;
      this.model = null;
      this.onInput();
    },
    fetchMissingLabels() {
      const ids = this.selectedIds.filter((id) => {
        const cached = this.valueLabelCache[id];
        return cached == null || cached === String(id);
      });
      if (ids.length === 0) return;
      ids.forEach((id) => {
        axios
          .get(`/v1/unit/${id}`)
          .then(({ data }) => {
            const item = (data && data.data) || data;
            if (item && item.id != null) {
              if (this.mainUnitOnly && !isMainUnit(item)) {
                this.removeUnitFromModel(item.id);
                return;
              }
              if (this.excludeDisabled && isUnitDisabledByDeactivation(item)) {
                this.removeDisabledUnitFromModel(item.id);
                return;
              }
              const { description, sirh, rems } = extractUnitFields(item);
              this.$set(
                this.valueLabelCache,
                item.id,
                formatUnitLabel(description, sirh, rems)
              );
            }
          })
          .catch(() => {});
      });
    },
    fetchData() {
      return new Promise((resolve, reject) => {
        const params = {
          q: this.search,
          max: this.limit,
          "page[n]": this.currentPage,
          sortBy: "description",
          sortDesc: false,
        };
        if (this.mainUnitOnly) params.is_main_unit = 1;
        axios
          .get("/v1/unit", {
            params,
          })
          .then(async ({ data }) => {
            this.meta = data.meta || null;
            const rawOptions = data.data || [];
            const sourceOptions = await this.filterUnitsByMainUnitWithFallback(rawOptions);
            const enrichedOptions = await this.enrichUnitsWithDetails(sourceOptions);
            this.options = this.filterUnitsByDisabledStatus(enrichedOptions);
            this.fillCacheFromOptions();
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    onSearch: debounce(async function (query, loading) {
      if (query && query.length) {
        loading(true);
        this.search = query;
        this.currentPage = 1;
        await this.fetchData();
        loading(false);
      } else {
        this.currentPage = 1;
        this.search = "";
        await this.fetchData();
      }
    }, 500),
    nextPage() {
      if (this.hasNextPage) {
        this.currentPage += 1;
      }
    },
    prevPage() {
      if (this.hasPrevPage) {
        this.currentPage -= 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  display: flex;
  justify-content: space-around;
  margin: 0.25rem 0.25rem 0;
}

.pagination button {
  flex-grow: 1;

  &:first-child {
    margin-right: 5px;
  }
}

.pagination button:hover {
  cursor: pointer;
}
</style>

<style lang="scss">
.units-select-paginated-wrapper .v-select.units-select-paginated .vs__selected-options,
.units-select-paginated-wrapper .v-select.units-select-paginated .vs__selected {
  max-height: 140px;
  overflow-y: auto;
  overflow-x: hidden;
  flex-wrap: wrap;
  align-content: flex-start;
}

.units-select-paginated-wrapper .v-select.units-select-paginated.units-select-paginated--dropup.vs--open .vs__dropdown-menu {
  top: auto;
  bottom: calc(100% - 1px);
}

.v-select.units-select-paginated .vs__dropdown-menu {
  z-index: 3000;
}
</style>
